import { useMemo, useState } from "react";
import type { Page } from "../types.js";
import { type NovelListSource, type NovelSummary } from "../apiClient.js";
import { sourceLabel } from "../lib/novel.js";
import { FilmCard } from "../components/FilmCard.js";
import { LibraryTabs } from "../components/LibraryTabs.js";
import { BlockedUsersManager } from "../components/BlockedUsersManager.js";
import { useLanguage, useT } from "../lib/i18n/store.js";
import { NovelListMore, type NovelListPagination } from "../components/NovelListMore.js";
import { ReelSelect } from "../components/ReelSelect.js";
import {
  PROFILE_SORT_OPTIONS,
  sortNovels,
  useFullListForSort,
  type NovelSortKey
} from "../lib/novelSort.js";

/** Which visibility bucket the "My Novels" list is filtered to. */
type VisibilityFilter = "all" | "public" | "draft";

// The list itself is App-owned state (one fetch per identity, shared with the
// landing hero); this page only renders it as a poster grid identical to the
// catalog. Management actions (edit, publish) live on a novel's detail page —
// opened by clicking a card — so "My Novels" stays a clean gallery of covers.
export function ProfilePage({
  pagination,
  novels,
  source,
  loading,
  currentOwnerId,
  onNavigate,
  onPlay,
  onOpenDetail,
  onCreate
}: {
  pagination?: NovelListPagination;
  novels: NovelSummary[];
  source: NovelListSource;
  loading: boolean;
  currentOwnerId: string | null;
  onNavigate: (page: Page) => void;
  onPlay: (novelId: string) => void;
  onOpenDetail: (novelId: string) => void;
  onCreate: () => void;
}) {
  const t = useT();
  const lang = useLanguage();
  // Published / drafts / all sub-filter over the owner's novels (client-side —
  // the list is already fully loaded per identity).
  const [visibility, setVisibility] = useState<VisibilityFilter>("all");
  // Same sort control as the author profile; "newest" is the list's own order,
  // page by page. Any other order loads the remaining pages first.
  const [sort, setSort] = useState<NovelSortKey>("newest");
  const sortPending = useFullListForSort(sort, "newest", pagination);
  const showSkeleton = loading || sortPending;
  const shownNovels = useMemo(
    () =>
      sortNovels(
        visibility === "all" ? novels : novels.filter((novel) => novel.visibility === visibility),
        sort
      ),
    [novels, visibility, sort]
  );
  const VISIBILITY_FILTERS: Array<{ key: VisibilityFilter; label: string }> = [
    { key: "all", label: t("library.filterAll") },
    { key: "public", label: t("library.filterPublished") },
    { key: "draft", label: t("library.filterDrafts") }
  ];

  return (
    <section className="page-section">
      <LibraryTabs active="profile" onNavigate={onNavigate} />
      <div className="section-heading">
        <h1>{t("profile.heading")}</h1>
        {/* The status line lives OUTSIDE the aria-busy region below — AT may
            withhold changes inside a busy subtree, and this line is the
            accessible loading signal (the skeletons are aria-hidden). */}
        <p className={source === "api" || loading ? "sr-only" : "metrics"} role="status">{t("profile.sourcePrefix")}{sourceLabel(source, loading, t("profile.offlineFallback"))}</p>
      </div>
      {/* Held in place while loading so the grid doesn't drop when it arrives. */}
      {loading && (
        <div className="library-toolbar" aria-hidden="true">
          <span className="skeleton library-filter-skeleton" />
          <span className="skeleton reel-select-skeleton" />
        </div>
      )}
      {!loading && novels.length > 0 && (
        <div className="library-toolbar">
          <div className="library-filter" role="tablist" aria-label={t("library.filterAria")}>
            {VISIBILITY_FILTERS.map((filter) => (
              <button
                key={filter.key}
                type="button"
                role="tab"
                aria-selected={visibility === filter.key}
                className={visibility === filter.key ? "library-tab is-active" : "library-tab"}
                onClick={() => setVisibility(filter.key)}
              >
                {filter.label}
              </button>
            ))}
          </div>
          {novels.length > 1 && (
            <div className="reel-toolbar-controls">
              <span className="reel-toolbar-label" aria-hidden="true">
                {t("author.sortLabel")}
              </span>
              <ReelSelect
                value={sort}
                options={PROFILE_SORT_OPTIONS.map((option) => ({ key: option.key, label: t(option.labelKey) }))}
                onChange={setSort}
                label={t("author.sortLabel")}
              />
            </div>
          )}
        </div>
      )}
      <div aria-busy={showSkeleton}>
      {showSkeleton ? (
        <div className="film-grid" data-testid="my-novels-skeleton" aria-hidden="true">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="film-card film-card--skeleton skeleton" />
          ))}
        </div>
      ) : novels.length === 0 ? (
        <div className="empty-state">
          <img src="/assets/empty-state.png" alt="" className="empty-state-art" />
          <p className="empty-state-message">{t("profile.emptyMessage")}</p>
          <p>{t("profile.emptyHint")}</p>
          <button type="button" className="empty-state-cta" onClick={onCreate}>
            {t("profile.createNovel")}
          </button>
        </div>
      ) : shownNovels.length === 0 ? (
        <p className="metrics library-filter-empty">{t("library.filterEmpty")}</p>
      ) : (
        <div className="film-grid">
          {shownNovels.map((novel) => (
            <FilmCard
              key={novel.id}
              novel={novel}
              lang={lang}
              t={t}
              onPlay={onPlay}
              onOpenDetail={onOpenDetail}
              badge={novel.visibility === "draft" ? t("library.draftBadge") : undefined}
            />
          ))}
        </div>
      )}
      </div>
      {currentOwnerId !== null && <BlockedUsersManager />}
      {!showSkeleton && <NovelListMore pagination={pagination} />}
    </section>
  );
}
